"use client";

import { cn } from "@marketing-sites/utils/cn";
import * as React from "react";

type TrailTestProps = {
  color: string;
  className?: string;
};

type Block = {
  x: number;
  y: number;
  width: number;
  height: number;
  targetWidth: number;
  targetHeight: number;
};

// Grid settings
const ROW_HEIGHT = 16;

// Block dimensions
const TARGET_WIDTH = 150;
const TARGET_HEIGHT = 12;

// Shape dimensions
const SMALL_SHAPE_WIDTH = 38;
const SMALL_SHAPE_HEIGHT = 8;
const LARGE_SHAPE_WIDTH = 50;
const LARGE_SHAPE_HEIGHT = 8;
const SHAPE_SIZE_THRESHOLD = 80;

// Animation settings
const ANIMATION_SPEED = 0.25;
const MOUSE_LAG_SPEED = 0.15; // How quickly the trail catches up to mouse (0-1)

// Block lifecycle
const MAX_VISIBLE_BLOCKS = 10;
const MIN_WIDTH_TO_SHRINK = 18;
const MIN_HEIGHT_TO_SHRINK = 10;
const REMOVAL_THRESHOLD = 1;

/**
 * Drawing functions
 */
const drawHorizontalLines = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  color: string,
) => {
  for (let y = 0; y < canvas.height; y += ROW_HEIGHT) {
    ctx.beginPath();
    // Offset by 0.5px to align stroke to pixel grid (avoids anti-aliasing blur)
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(canvas.width, y + 0.5);
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.stroke();
  }
};

/**
 * Draw small stepped shape (for blocks < 50px wide)
 * Based on SVG: 38x8 with stepped edges
 */
const drawSmallShape = (ctx: CanvasRenderingContext2D, block: Block) => {
  const scaleX = block.width / SMALL_SHAPE_WIDTH;
  const scaleY = block.height / SMALL_SHAPE_HEIGHT;
  const offsetX = block.x - block.width / 2;
  const offsetY = block.y - block.height / 2;

  // Helper to transform SVG coords to canvas coords
  const tx = (x: number) => offsetX + x * scaleX;
  const ty = (y: number) => offsetY + y * scaleY;

  ctx.beginPath();
  // M28.9971 0
  ctx.moveTo(tx(28.9971), ty(0));
  // V1.5
  ctx.lineTo(tx(28.9971), ty(1.5));
  // H33.9971
  ctx.lineTo(tx(33.9971), ty(1.5));
  // V2.75
  ctx.lineTo(tx(33.9971), ty(2.75));
  // H37.9941
  ctx.lineTo(tx(37.9941), ty(2.75));
  // V5.25
  ctx.lineTo(tx(37.9941), ty(5.25));
  // H33.9971
  ctx.lineTo(tx(33.9971), ty(5.25));
  // V6.5
  ctx.lineTo(tx(33.9971), ty(6.5));
  // H28.9971
  ctx.lineTo(tx(28.9971), ty(6.5));
  // V8
  ctx.lineTo(tx(28.9971), ty(8));
  // H8.99707
  ctx.lineTo(tx(8.99707), ty(8));
  // V6.5
  ctx.lineTo(tx(8.99707), ty(6.5));
  // H4
  ctx.lineTo(tx(4), ty(6.5));
  // V5.25
  ctx.lineTo(tx(4), ty(5.25));
  // H0
  ctx.lineTo(tx(0), ty(5.25));
  // V2.75
  ctx.lineTo(tx(0), ty(2.75));
  // H4
  ctx.lineTo(tx(4), ty(2.75));
  // V1.5
  ctx.lineTo(tx(4), ty(1.5));
  // H8.99707
  ctx.lineTo(tx(8.99707), ty(1.5));
  // V0
  ctx.lineTo(tx(8.99707), ty(0));
  // Z (close path back to start)
  ctx.closePath();
  ctx.fill();
};

/**
 * Draw large stepped shape (for blocks >= 50px wide)
 * Based on SVG: 50x8 with simpler stepped edges
 */
const drawLargeShape = (ctx: CanvasRenderingContext2D, block: Block) => {
  const scaleX = block.width / LARGE_SHAPE_WIDTH;
  const scaleY = block.height / LARGE_SHAPE_HEIGHT;
  const offsetX = block.x - block.width / 2;
  const offsetY = block.y - block.height / 2;

  // Helper to transform SVG coords to canvas coords
  const tx = (x: number) => offsetX + x * scaleX;
  const ty = (y: number) => offsetY + y * scaleY;

  ctx.beginPath();
  // M44.9893 0
  ctx.moveTo(tx(44.9893), ty(0));
  // V1.5
  ctx.lineTo(tx(44.9893), ty(1.5));
  // H49.9785
  ctx.lineTo(tx(49.9785), ty(1.5));
  // V6.5
  ctx.lineTo(tx(49.9785), ty(6.5));
  // H44.9893
  ctx.lineTo(tx(44.9893), ty(6.5));
  // V8
  ctx.lineTo(tx(44.9893), ty(8));
  // H4.98926
  ctx.lineTo(tx(4.98926), ty(8));
  // V6.5
  ctx.lineTo(tx(4.98926), ty(6.5));
  // H0
  ctx.lineTo(tx(0), ty(6.5));
  // V1.5
  ctx.lineTo(tx(0), ty(1.5));
  // H4.98926
  ctx.lineTo(tx(4.98926), ty(1.5));
  // V0
  ctx.lineTo(tx(4.98926), ty(0));
  // Z (close path back to start)
  ctx.closePath();
  ctx.fill();
};

/**
 * Draw block shape - chooses between small and large shape based on width
 */
const drawBlockShape = (ctx: CanvasRenderingContext2D, block: Block) => {
  if (block.width < SHAPE_SIZE_THRESHOLD) {
    drawSmallShape(ctx, block);
  } else {
    drawLargeShape(ctx, block);
  }
};

export const CursorTrail = ({ color = "black", className }: TrailTestProps) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const blocksRef = React.useRef<Block[]>([]);
  const lastSpawnBlockRef = React.useRef<{ x: number; y: number } | null>(null);
  const mouseStopTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const currentYRef = React.useRef<number | null>(null);

  // Mouse lag tracking
  const targetMouseRef = React.useRef<{ x: number; y: number } | null>(null);
  const laggedMouseRef = React.useRef<{ x: number; y: number } | null>(null);
  const isMouseInsideRef = React.useRef(false);

  // Delta time tracking for consistent animation across refresh rates
  const lastTimeRef = React.useRef<number>(0);

  const shrinkAllBlocks = () => {
    blocksRef.current.forEach((block) => {
      block.targetWidth = 0;
      block.targetHeight = 0;
    });
    lastSpawnBlockRef.current = null;
  };

  const shrinkBlocksExceptCurrentRow = () => {
    const currentY = currentYRef.current;
    blocksRef.current.forEach((block) => {
      if (block.y !== currentY) {
        block.targetWidth = 0;
        block.targetHeight = 0;
      }
    });
  };

  const spawnBlock = (x: number, y: number) => {
    blocksRef.current.push({
      x,
      y,
      width: 0,
      height: 0,
      targetWidth: TARGET_WIDTH,
      targetHeight: TARGET_HEIGHT,
    });

    // Mark oldest blocks to shrink when we have too many
    const excessCount = blocksRef.current.length - MAX_VISIBLE_BLOCKS;
    for (let i = 0; i < excessCount; i++) {
      const block = blocksRef.current[i];
      if (
        block.width > MIN_WIDTH_TO_SHRINK &&
        block.height > MIN_HEIGHT_TO_SHRINK
      ) {
        block.targetWidth = 0;
        block.targetHeight = 0;
      }
    }
  };

  const updateBlocks = (dt: number) => {
    blocksRef.current.forEach((block) => {
      block.width += (block.targetWidth - block.width) * ANIMATION_SPEED * dt;
      block.height +=
        (block.targetHeight - block.height) * ANIMATION_SPEED * dt;
    });

    // Remove blocks that have fully shrunk
    blocksRef.current = blocksRef.current.filter(
      (block) => block.width > REMOVAL_THRESHOLD,
    );
  };

  const drawBlocks = (ctx: CanvasRenderingContext2D) => {
    blocksRef.current.forEach((block) => {
      ctx.fillStyle = color;
      drawBlockShape(ctx, block);
    });
  };

  const snapToRow = (y: number) => Math.floor(y / ROW_HEIGHT) * ROW_HEIGHT;

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Check if this is a touch device
    const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };

    const isInsideCanvas = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      return (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );
    };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();

      // Scale mouse position from CSS display size to canvas internal size
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;

      // Reset mouse stop timeout
      if (mouseStopTimeoutRef.current) {
        clearTimeout(mouseStopTimeoutRef.current);
      }

      // Check if mouse is inside canvas bounds
      if (!isInsideCanvas(e)) {
        isMouseInsideRef.current = false;
        if (lastSpawnBlockRef.current !== null) {
          shrinkAllBlocks();
        }
        return;
      }

      isMouseInsideRef.current = true;
      targetMouseRef.current = { x, y };

      // Initialize lagged position if needed
      if (!laggedMouseRef.current) {
        laggedMouseRef.current = { x, y };
      }

      // Set timeout to shrink non-current-row blocks when mouse stops
      mouseStopTimeoutRef.current = setTimeout(
        shrinkBlocksExceptCurrentRow,
        150,
      );
    };

    const updateLaggedMouse = (dt: number) => {
      if (
        !targetMouseRef.current ||
        !laggedMouseRef.current ||
        !isMouseInsideRef.current
      ) {
        return;
      }

      // Lerp lagged position towards target
      laggedMouseRef.current.x +=
        (targetMouseRef.current.x - laggedMouseRef.current.x) *
        MOUSE_LAG_SPEED *
        dt;
      laggedMouseRef.current.y +=
        (targetMouseRef.current.y - laggedMouseRef.current.y) *
        MOUSE_LAG_SPEED *
        dt;

      const x = laggedMouseRef.current.x;
      const currentY = snapToRow(laggedMouseRef.current.y);
      const lastY = lastSpawnBlockRef.current?.y;

      // Store current Y for the stop handler
      currentYRef.current = currentY;

      if (lastY === null || lastY === undefined) {
        spawnBlock(x, currentY);
      } else if (currentY === lastY) {
        // Moving horizontally on same row
        spawnBlock(x, currentY);
      } else {
        // Fill all rows between last and current
        const step = currentY > lastY ? ROW_HEIGHT : -ROW_HEIGHT;
        for (
          let row = lastY + step;
          step > 0 ? row <= currentY : row >= currentY;
          row += step
        ) {
          spawnBlock(x, row);
        }
      }

      lastSpawnBlockRef.current = { x, y: currentY };
    };

    const render = (currentTime: number) => {
      // Initialize lastTime on first frame
      if (lastTimeRef.current === 0) {
        lastTimeRef.current = currentTime;
      }

      // Calculate delta time normalized to 30fps (33.33ms per frame)
      const deltaTime = currentTime - lastTimeRef.current;
      lastTimeRef.current = currentTime;
      // Clamp dt to prevent large jumps after tab switch
      const dt = Math.min(deltaTime / 33.33, 3);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      updateLaggedMouse(dt);
      updateBlocks(dt);
      drawBlocks(ctx);
      drawHorizontalLines(ctx, canvas, color);
      window.requestAnimationFrame(render);
    };

    // Set initial size
    resizeCanvas();

    // Listen for resize and scroll (scroll can affect layout)
    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("scroll", resizeCanvas, { passive: true });

    // On touch devices, just draw horizontal lines without mouse interactivity
    if (isTouchDevice) {
      const drawStatic = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawHorizontalLines(ctx, canvas, color);
      };

      drawStatic();

      // Redraw on resize and scroll
      const handleResizeOrScroll = () => {
        resizeCanvas();
        drawStatic();
      };
      window.addEventListener("resize", handleResizeOrScroll);
      window.addEventListener("scroll", handleResizeOrScroll, {
        passive: true,
      });

      return () => {
        window.removeEventListener("resize", resizeCanvas);
        window.removeEventListener("resize", handleResizeOrScroll);
        window.removeEventListener("scroll", resizeCanvas);
        window.removeEventListener("scroll", handleResizeOrScroll);
      };
    }

    // Listen on window so events flow through elements above canvas
    window.addEventListener("mousemove", handleMouseMove);

    // Start the render loop
    const animationId = window.requestAnimationFrame(render);

    return () => {
      window.cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("scroll", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
      if (mouseStopTimeoutRef.current) {
        clearTimeout(mouseStopTimeoutRef.current);
      }
    };
  }, []);

  return (
    <canvas
      className={cn(
        "pointer-events-none absolute inset-0 block h-full w-full",
        className,
      )}
      ref={canvasRef}
    />
  );
};
